import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import io from 'socket.io-client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Radio, Video, Users, Play, StopCircle, Mic, MicOff, VideoIcon, VideoOff, Monitor, MonitorOff } from 'lucide-react';
import {
  createPeerConnection,
  getUserMedia,
  createOffer,
  handleAnswer,
  addIceCandidate
} from '@/utils/webrtc';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LiveStreamManagement = () => {
  const { t } = useTranslation();
  const [activeStream, setActiveStream] = useState(null);
  const [streams, setStreams] = useState([]);
  const [students, setStudents] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showStreamDialog, setShowStreamDialog] = useState(false);
  const [showAddStudentsDialog, setShowAddStudentsDialog] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [newStudentsToAdd, setNewStudentsToAdd] = useState([]);
  const [form, setForm] = useState({ title: '', description: '' });
  
  // WebRTC states
  const [isStreaming, setIsStreaming] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [connectedPeers, setConnectedPeers] = useState(new Map());
  const [viewerCount, setViewerCount] = useState(0);
  
  // Refs
  const socketRef = useRef(null);
  const localVideoRef = useRef(null);
  const peerConnectionsRef = useRef(new Map());
  const userRef = useRef(null);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      userRef.current = JSON.parse(userStr);
    }
    
    fetchActiveStream();
    fetchStreams();
    fetchStudents();
    
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (localStream && localVideoRef.current) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  const cleanup = () => {
    console.log('🧹 Cleaning up stream resources...');
    
    // Stop all tracks
    if (localStream) {
      console.log('⏹️ Stopping local stream tracks...');
      localStream.getTracks().forEach(track => {
        console.log(`  Stopping ${track.kind} track`);
        track.stop();
      });
    }
    if (screenStream) {
      console.log('⏹️ Stopping screen stream tracks...');
      screenStream.getTracks().forEach(track => track.stop());
    }
    
    // Close all peer connections
    console.log(`🔌 Closing ${peerConnectionsRef.current.size} peer connection(s)...`);
    peerConnectionsRef.current.forEach((pc, sid) => {
      console.log(`  Closing connection to ${sid}`);
      pc.close();
    });
    peerConnectionsRef.current.clear();
    
    // Disconnect socket
    if (socketRef.current) {
      console.log('🔌 Disconnecting socket...');
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    console.log('✅ Cleanup complete');
  };

  const fetchActiveStream = async () => {
    try {
      console.log('🔄 Fetching active stream...');
      const response = await axios.get(`${API}/teacher/live-streams/active`);
      console.log('📡 Active stream response:', response.data);
      setActiveStream(response.data.stream);
      
      if (response.data.stream) {
        console.log('⚠️ Found active stream:', response.data.stream.id, response.data.stream.title);
      } else {
        console.log('✅ No active stream');
      }
    } catch (error) {
      console.error('❌ Error fetching active stream:', error);
    }
  };

  const fetchStreams = async () => {
    try {
      const response = await axios.get(`${API}/teacher/live-streams`);
      setStreams(response.data.streams);
    } catch (error) {
      console.error('Error fetching streams:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/teacher/students`);
      setStudents(response.data.students);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const initializeSocket = (roomName) => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    socketRef.current = io(BACKEND_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current.on('connect', () => {
      console.log('Socket connected:', socketRef.current.id);
      
      // Join the room
      socketRef.current.emit('join_room', {
        room_name: roomName,
        user_id: userRef.current.id,
        user_name: userRef.current.name,
        user_role: 'teacher'
      });
    });

    socketRef.current.on('room-joined', async (data) => {
      console.log('Joined room:', data);
      toast.success(t('joinedStream') || 'انضممت للبث');
      
      // Create offers for all existing users
      for (const user of data.other_users) {
        await createPeerConnectionForUser(user.user_id, user.sid, true);
      }
    });

    socketRef.current.on('user-joined', async (data) => {
      console.log('User joined:', data);
      setViewerCount(prev => prev + 1);
      
      // Create peer connection and send offer to the new user
      await createPeerConnectionForUser(data.user_id, data.sid, true);
    });

    socketRef.current.on('user-left', (data) => {
      console.log('User left:', data);
      setViewerCount(prev => Math.max(0, prev - 1));
      
      // Close peer connection
      const pc = peerConnectionsRef.current.get(data.sid);
      if (pc) {
        pc.close();
        peerConnectionsRef.current.delete(data.sid);
      }
    });

    socketRef.current.on('offer', async (data) => {
      console.log('Received offer from:', data.sender_sid);
      await handleIncomingOffer(data.sender_id, data.sender_sid, data.offer);
    });

    socketRef.current.on('answer', async (data) => {
      console.log('Received answer from:', data.sender_sid);
      const pc = peerConnectionsRef.current.get(data.sender_sid);
      if (pc) {
        await handleAnswer(pc, data.answer);
      }
    });

    socketRef.current.on('ice_candidate', async (data) => {
      const pc = peerConnectionsRef.current.get(data.sender_sid);
      if (pc && data.candidate) {
        await addIceCandidate(pc, data.candidate);
      }
    });

    socketRef.current.on('error', (data) => {
      console.error('Socket error:', data);
      toast.error(data.message || t('error'));
    });

    socketRef.current.on('disconnect', () => {
      console.log('Socket disconnected');
    });
  };

  const createPeerConnectionForUser = async (userId, sid, shouldCreateOffer) => {
    console.log(`🔧 Creating peer connection for user ${userId} (${sid})`);
    
    if (peerConnectionsRef.current.has(sid)) {
      console.log('⚠️ Peer connection already exists for', sid);
      return;
    }

    const pc = createPeerConnection(
      (candidate) => {
        // Send ICE candidate
        console.log(`📤 Sending ICE candidate to ${sid}`);
        socketRef.current.emit('ice_candidate', {
          target_sid: sid,
          candidate: candidate,
          sender_id: userRef.current.id
        });
      },
      (remoteStream) => {
        console.log('📥 Received remote stream from:', sid);
      }
    );

    peerConnectionsRef.current.set(sid, pc);

    // Add local tracks
    if (localStream) {
      console.log('📹 Adding local stream tracks...');
      let trackCount = 0;
      localStream.getTracks().forEach(track => {
        console.log(`  Adding ${track.kind} track: ${track.label}`);
        pc.addTrack(track, localStream);
        trackCount++;
      });
      console.log(`✅ Added ${trackCount} local tracks`);
    } else {
      console.warn('⚠️ No local stream available!');
    }

    // Add screen tracks if sharing
    if (screenStream) {
      console.log('🖥️ Adding screen stream tracks...');
      screenStream.getTracks().forEach(track => {
        console.log(`  Adding screen ${track.kind} track`);
        pc.addTrack(track, screenStream);
      });
    }

    if (shouldCreateOffer) {
      console.log('📤 Creating and sending offer to', sid);
      try {
        const offer = await createOffer(pc);
        socketRef.current.emit('offer', {
          target_sid: sid,
          offer: offer,
          sender_id: userRef.current.id
        });
        console.log('✅ Offer sent to', sid);
      } catch (error) {
        console.error('❌ Error creating offer:', error);
      }
    }

    return pc;
  };

  const handleIncomingOffer = async (senderId, senderSid, offer) => {
    let pc = peerConnectionsRef.current.get(senderSid);
    
    if (!pc) {
      pc = await createPeerConnectionForUser(senderId, senderSid, false);
    }

    await pc.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    socketRef.current.emit('answer', {
      target_sid: senderSid,
      answer: answer,
      sender_id: userRef.current.id
    });
  };

  const handleCreateStream = async () => {
    console.log('🚀 handleCreateStream called!');
    console.log('Form data:', form);
    console.log('Selected students:', selectedStudents);
    
    if (!form.title) {
      console.log('❌ Title is empty');
      toast.error(t('fillAllFields') || 'يرجى ملء جميع الحقول');
      return;
    }

    try {
      console.log('✅ Title is valid, creating stream...');
      console.log('API URL:', API);
      console.log('Creating stream with data:', {
        title: form.title,
        description: form.description,
        student_emails: selectedStudents
      });

      const response = await axios.post(`${API}/teacher/live-streams`, {
        title: form.title,
        description: form.description,
        student_emails: selectedStudents
      });

      console.log('✅ Stream created:', response.data);
      toast.success(t('streamCreated') || 'تم إنشاء البث المباشر');
      setShowCreateDialog(false);
      
      const newStream = response.data.stream;
      setActiveStream(newStream);
      setForm({ title: '', description: '' });
      setSelectedStudents([]);
      
      // Start streaming
      console.log('📹 Starting streaming for room:', newStream.room_name);
      await startStreaming(newStream);
      
      fetchStreams();
    } catch (error) {
      console.error('❌ Error creating stream:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  const startStreaming = async (stream) => {
    try {
      console.log('Requesting camera and microphone access...');
      
      // Get camera and microphone
      const mediaStream = await getUserMedia({ video: true, audio: true });
      console.log('Media stream obtained:', mediaStream);
      
      setLocalStream(mediaStream);
      setIsStreaming(true);
      setShowStreamDialog(true);
      
      // Initialize socket and signaling
      console.log('Initializing socket for room:', stream.room_name);
      initializeSocket(stream.room_name);
      
      toast.success(t('streamStarted') || 'بدأ البث المباشر');
    } catch (error) {
      console.error('Error starting stream:', error);
      console.error('Error details:', error.message);
      
      // More specific error messages
      if (error.name === 'NotAllowedError') {
        toast.error(t('cameraPermissionDenied') || 'تم رفض الوصول للكاميرا والميكروفون. يرجى السماح بالوصول.');
      } else if (error.name === 'NotFoundError') {
        toast.error(t('cameraNotFound') || 'لم يتم العثور على كاميرا أو ميكروفون.');
      } else {
        toast.error(t('errorAccessingMedia') || 'خطأ في الوصول للكاميرا أو الميكروفون');
      }
      
      // Cleanup on error
      setIsStreaming(false);
      setActiveStream(null);
    }
  };

  const handleEndStream = async () => {
    console.log('🛑 Ending stream...');
    
    if (!activeStream) {
      console.warn('⚠️ No active stream to end');
      return;
    }

    try {
      console.log('📤 Calling API to end stream:', activeStream.id);
      await axios.put(`${API}/teacher/live-streams/${activeStream.id}/end`);
      console.log('✅ API call successful');
      
      // Cleanup resources
      cleanup();
      
      // Reset states
      setIsStreaming(false);
      setLocalStream(null);
      setScreenStream(null);
      setActiveStream(null);
      setShowStreamDialog(false);
      setViewerCount(0);
      setIsMuted(false);
      setIsVideoOff(false);
      setIsSharingScreen(false);
      
      toast.success(t('streamEnded') || 'تم إنهاء البث');
      
      // Refresh stream list
      fetchStreams();
      fetchActiveStream();
      
      console.log('✅ Stream ended successfully');
    } catch (error) {
      console.error('❌ Error ending stream:', error);
      toast.error(t('error'));
      
      // Even if API fails, try to cleanup locally
      cleanup();
      setIsStreaming(false);
      setActiveStream(null);
      setShowStreamDialog(false);
    }
  };

  const handleAddStudents = async () => {
    if (!activeStream || newStudentsToAdd.length === 0) {
      toast.error(t('pleaseSelectStudents') || 'يرجى اختيار طلاب');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/teacher/live-streams/${activeStream.id}/add-students`,
        newStudentsToAdd
      );
      
      toast.success(`${t('studentsAdded') || 'تم إضافة الطلاب'}: ${response.data.added_count}`);
      setShowAddStudentsDialog(false);
      setNewStudentsToAdd([]);
    } catch (error) {
      console.error('Error adding students:', error);
      toast.error(t('error'));
    }
  };

  const toggleMute = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOff(!videoTrack.enabled);
      }
    }
  };

  const toggleScreenShare = async () => {
    try {
      if (!isSharingScreen) {
        // Start screen sharing
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: false
        });
        
        setScreenStream(stream);
        setIsSharingScreen(true);
        
        // Replace video track in all peer connections
        const screenTrack = stream.getVideoTracks()[0];
        peerConnectionsRef.current.forEach(pc => {
          const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
          if (sender) {
            sender.replaceTrack(screenTrack);
          }
        });
        
        // Handle screen share stop
        screenTrack.onended = () => {
          stopScreenShare();
        };
        
        toast.success(t('screenShareStarted') || 'بدأت مشاركة الشاشة');
      } else {
        stopScreenShare();
      }
    } catch (error) {
      console.error('Error toggling screen share:', error);
      toast.error(t('errorSharingScreen') || 'خطأ في مشاركة الشاشة');
    }
  };

  const stopScreenShare = () => {
    if (screenStream) {
      screenStream.getTracks().forEach(track => track.stop());
      setScreenStream(null);
    }
    
    setIsSharingScreen(false);
    
    // Restore camera video track
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      peerConnectionsRef.current.forEach(pc => {
        const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
        if (sender) {
          sender.replaceTrack(videoTrack);
        }
      });
    }
    
    toast.success(t('screenShareStopped') || 'توقفت مشاركة الشاشة');
  };

  const toggleStudent = (email) => {
    if (selectedStudents.includes(email)) {
      setSelectedStudents(selectedStudents.filter(e => e !== email));
    } else {
      setSelectedStudents([...selectedStudents, email]);
    }
  };

  const selectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.email));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Radio className="h-8 w-8 text-red-500" />
            {t('liveStream') || 'البث المباشر'}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('manageLiveStreams') || 'إدارة جلسات البث المباشر'}
          </p>
        </div>

        <div className="flex gap-2">
          {activeStream && isStreaming ? (
            <Button
              onClick={() => setShowStreamDialog(true)}
              className="bg-gradient-to-r from-red-600 to-pink-600 animate-pulse"
            >
              <Video className="h-4 w-4 mr-2" />
              {t('backToStream') || 'العودة للبث'}
            </Button>
          ) : (
            <Button
              onClick={() => {
                console.log('🔴 Start New Stream button clicked');
                console.log('Active stream state:', activeStream);
                setShowCreateDialog(true);
              }}
              className="bg-gradient-to-r from-red-600 to-pink-600"
              disabled={activeStream !== null}
            >
              <Play className="h-4 w-4 mr-2" />
              {t('startNewStream') || 'بدء بث جديد'}
            </Button>
          )}
          
          {activeStream && !isStreaming && (
            <Button
              onClick={async () => {
                console.log('🔨 Force end stream clicked');
                try {
                  await axios.put(`${API}/teacher/live-streams/${activeStream.id}/end`);
                  setActiveStream(null);
                  toast.success(t('streamEnded') || 'تم إنهاء البث');
                  fetchStreams();
                } catch (error) {
                  console.error('Error ending stream:', error);
                  // Even if API fails, clear the local state
                  setActiveStream(null);
                  toast.success(t('streamCleared') || 'تم مسح البث المحلي');
                }
              }}
              variant="destructive"
            >
              <StopCircle className="h-4 w-4 mr-2" />
              {t('forceEndStream') || 'إنهاء البث بالقوة'}
            </Button>
          )}
        </div>
      </div>

      {/* Active Stream Banner */}
      {activeStream && isStreaming && (
        <Card className="border-red-500 border-2 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                  <div className="h-3 w-3 bg-red-600 rounded-full animate-pulse" />
                  {t('liveNow') || 'مباشر الآن'}
                </h3>
                <p className="text-red-700 font-medium">{activeStream.title}</p>
                <p className="text-sm text-red-600 mt-1">
                  {viewerCount} {t('viewers') || 'مشاهد'}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowStreamDialog(true)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Video className="h-4 w-4 mr-2" />
                  {t('viewStream') || 'عرض البث'}
                </Button>
                <Button
                  onClick={handleEndStream}
                  variant="destructive"
                >
                  <StopCircle className="h-4 w-4 mr-2" />
                  {t('endStream') || 'إنهاء البث'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stream History */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold">{t('streamHistory') || 'سجل البث'}</h3>
          <Button
            onClick={() => {
              fetchActiveStream();
              fetchStreams();
              toast.success(t('refreshed') || 'تم التحديث');
            }}
            variant="outline"
            size="sm"
          >
            {t('refresh') || 'تحديث'}
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {streams.map(stream => (
            <Card key={stream.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{stream.title}</span>
                  {stream.is_active && (
                    <span className="text-sm bg-red-100 text-red-700 px-2 py-1 rounded">
                      {t('active') || 'نشط'}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-2">{stream.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {stream.students_count} {t('students')}
                    </span>
                    <span>
                      {new Date(stream.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {stream.is_active && (
                    <Button
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/teacher/live-streams/${stream.id}/end`);
                          toast.success(t('streamEnded') || 'تم إنهاء البث');
                          fetchActiveStream();
                          fetchStreams();
                        } catch (error) {
                          console.error('Error ending stream:', error);
                          toast.error(t('error'));
                        }
                      }}
                      variant="destructive"
                      size="sm"
                    >
                      <StopCircle className="h-3 w-3 mr-1" />
                      {t('endStream') || 'إنهاء'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Create Stream Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('createLiveStream') || 'إنشاء بث مباشر'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>{t('title')}</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={t('enterStreamTitle') || 'أدخل عنوان البث'}
              />
            </div>

            <div>
              <Label>{t('description')}</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t('enterStreamDescription') || 'أدخل وصف البث'}
                rows={3}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{t('selectStudents')}</Label>
                <Button onClick={selectAll} variant="outline" size="sm">
                  {selectedStudents.length === students.length ? t('deselectAll') : t('selectAll')}
                </Button>
              </div>

              <div className="border rounded-lg p-4 max-h-60 overflow-y-auto space-y-2">
                {students.map(student => (
                  <div
                    key={student.email}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    onClick={() => toggleStudent(student.email)}
                  >
                    <Checkbox
                      checked={selectedStudents.includes(student.email)}
                      onCheckedChange={() => toggleStudent(student.email)}
                    />
                    <div>
                      <p className="font-medium">{student.name}</p>
                      <p className="text-sm text-gray-500">{student.email}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {selectedStudents.length} {t('studentsSelected') || 'طالب محدد'}
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => {
                  console.log('🔴 Button CLICKED!');
                  handleCreateStream();
                }}
                className="flex-1 bg-gradient-to-r from-red-600 to-pink-600"
              >
                <Play className="h-4 w-4 mr-2" />
                {t('startStream') || 'بدء البث'}
              </Button>
              <Button
                onClick={() => setShowCreateDialog(false)}
                variant="outline"
                className="flex-1"
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Live Stream Dialog */}
      {activeStream && isStreaming && (
        <Dialog open={showStreamDialog} onOpenChange={setShowStreamDialog}>
          <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] p-0 m-0 rounded-none">
            {/* Header Overlay */}
            <div className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/80 to-transparent p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 bg-red-600 rounded-full animate-pulse" />
                  <span className="text-white font-semibold text-lg">
                    {activeStream.title}
                  </span>
                  <span className="text-white/80 text-sm">
                    ({viewerCount} {t('viewers') || 'مشاهد'})
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowAddStudentsDialog(true)}
                    variant="outline"
                    size="default"
                    className="bg-white/10 hover:bg-white/20 text-white border-white/30"
                  >
                    <Users className="h-5 w-5 mr-2" />
                    {t('addStudents') || 'إضافة متدربين'}
                  </Button>
                  <Button
                    onClick={handleEndStream}
                    variant="destructive"
                    size="default"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <StopCircle className="h-5 w-5 mr-2" />
                    {t('endStream') || 'إنهاء البث'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Video Full Screen */}
            <div className="w-full h-full bg-black relative">
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-contain"
              />
              
              {/* Controls Overlay - Larger and Better Positioned */}
              <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-4 bg-black/70 backdrop-blur-md rounded-2xl px-8 py-4 shadow-2xl">
                <Button
                  onClick={toggleMute}
                  variant={isMuted ? 'destructive' : 'secondary'}
                  size="lg"
                  className="rounded-full w-14 h-14 p-0"
                  title={isMuted ? t('unmute') : t('mute')}
                >
                  {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
                </Button>
                
                <Button
                  onClick={toggleVideo}
                  variant={isVideoOff ? 'destructive' : 'secondary'}
                  size="lg"
                  className="rounded-full w-14 h-14 p-0"
                  title={isVideoOff ? t('turnOnVideo') : t('turnOffVideo')}
                >
                  {isVideoOff ? <VideoOff className="h-6 w-6" /> : <VideoIcon className="h-6 w-6" />}
                </Button>
                
                <Button
                  onClick={toggleScreenShare}
                  variant={isSharingScreen ? 'default' : 'secondary'}
                  size="lg"
                  className="rounded-full w-14 h-14 p-0 bg-blue-600 hover:bg-blue-700"
                  title={isSharingScreen ? t('stopScreenShare') : t('shareScreen')}
                >
                  {isSharingScreen ? <MonitorOff className="h-6 w-6" /> : <Monitor className="h-6 w-6" />}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Add Students Dialog */}
      <Dialog open={showAddStudentsDialog} onOpenChange={setShowAddStudentsDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('addStudentsToStream') || 'إضافة متدربين للبث'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('selectStudentsToAdd') || 'اختر المتدربين الذين تريد إضافتهم للبث المباشر'}
            </p>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>{t('selectStudents')}</Label>
                <Button 
                  onClick={() => {
                    if (newStudentsToAdd.length === students.length) {
                      setNewStudentsToAdd([]);
                    } else {
                      setNewStudentsToAdd(students.map(s => s.email));
                    }
                  }} 
                  variant="outline" 
                  size="sm"
                >
                  {newStudentsToAdd.length === students.length ? t('deselectAll') : t('selectAll')}
                </Button>
              </div>

              <div className="border rounded-lg p-4 max-h-60 overflow-y-auto space-y-2">
                {students.map(student => (
                  <div
                    key={student.email}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    onClick={() => {
                      if (newStudentsToAdd.includes(student.email)) {
                        setNewStudentsToAdd(newStudentsToAdd.filter(e => e !== student.email));
                      } else {
                        setNewStudentsToAdd([...newStudentsToAdd, student.email]);
                      }
                    }}
                  >
                    <Checkbox
                      checked={newStudentsToAdd.includes(student.email)}
                      onCheckedChange={() => {
                        if (newStudentsToAdd.includes(student.email)) {
                          setNewStudentsToAdd(newStudentsToAdd.filter(e => e !== student.email));
                        } else {
                          setNewStudentsToAdd([...newStudentsToAdd, student.email]);
                        }
                      }}
                    />
                    <div>
                      <p className="font-medium">{student.name}</p>
                      <p className="text-sm text-gray-500">{student.email}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {newStudentsToAdd.length} {t('studentsSelected') || 'طالب محدد'}
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleAddStudents}
                className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600"
                disabled={newStudentsToAdd.length === 0}
              >
                <Users className="h-4 w-4 mr-2" />
                {t('addToStream') || 'إضافة للبث'}
              </Button>
              <Button
                onClick={() => {
                  setShowAddStudentsDialog(false);
                  setNewStudentsToAdd([]);
                }}
                variant="outline"
                className="flex-1"
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveStreamManagement;
